import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type { ApprovalQueueTask } from "serviceStores/mock/approval-queue.mock";
import type { Plant } from "serviceStores/mock/plants.mock";

/**
 * Approval Queue Store
 *
 * Manages the approval queue view - POs pending approval.
 * Used by approvers (Clay's queue).
 */
class ApprovalQueueStore {
  rootStore: RootStore;

  // Filters
  plantFilter: number | null = null;
  vendorFilter: string = "";
  searchTerm: string = "";
  statusFilter: "all" | "pending" | "completed" = "pending";
  priorityFilter: "all" | "urgent" | "normal" = "all";
  amountFilter: "all" | "under1000" | "1000to5000" | "over5000" = "all";

  // Sort
  sortField: "createdDate" | "totalAmount" | "vendorName" | "priority" =
    "createdDate";
  sortDirection: "asc" | "desc" = "asc";

  // UI State
  loading = false;
  error: string | null = null;
  selectedTaskIds: number[] = [];

  // Reference Data
  plants: Plant[] = [];
  tasks: ApprovalQueueTask[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Filtered & Sorted Data ====================

  get filteredTasks(): ApprovalQueueTask[] {
    let filtered = this.tasks.filter((t) => t.queueType === "approval");

    // Status filter
    if (this.statusFilter === "pending") {
      filtered = filtered.filter((t) => t.status === "pending");
    } else if (this.statusFilter === "completed") {
      filtered = filtered.filter((t) => t.status === "completed");
    }

    // Plant filter
    if (this.plantFilter) {
      filtered = filtered.filter((t) => t.plantId === this.plantFilter);
    }

    // Priority filter
    if (this.priorityFilter !== "all") {
      filtered = filtered.filter((t) => t.priority === this.priorityFilter);
    }

    // Amount filter
    if (this.amountFilter !== "all") {
      filtered = filtered.filter((t) => {
        const amount = t.totalAmount || 0;
        switch (this.amountFilter) {
          case "under1000":
            return amount < 1000;
          case "1000to5000":
            return amount >= 1000 && amount <= 5000;
          case "over5000":
            return amount > 5000;
          default:
            return true;
        }
      });
    }

    // Vendor filter
    if (this.vendorFilter) {
      filtered = filtered.filter((t) =>
        t.vendorName?.toLowerCase().includes(this.vendorFilter.toLowerCase())
      );
    }

    // Search
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.entityNumber?.toLowerCase().includes(search) ||
          t.vendorName?.toLowerCase().includes(search) ||
          t.title?.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (this.sortField) {
        case "createdDate":
          aVal = a.createdDate || "";
          bVal = b.createdDate || "";
          break;
        case "totalAmount":
          aVal = a.totalAmount || 0;
          bVal = b.totalAmount || 0;
          break;
        case "vendorName":
          aVal = a.vendorName || "";
          bVal = b.vendorName || "";
          break;
        case "priority":
          aVal = a.priority === "urgent" ? 0 : 1;
          bVal = b.priority === "urgent" ? 0 : 1;
          break;
        default:
          aVal = a.createdDate || "";
          bVal = b.createdDate || "";
      }

      if (this.sortDirection === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }

  get tasksByPlant(): Array<{ plant: Plant; count: number; urgent: number }> {
    return this.plants.map((plant) => {
      const plantTasks = this.tasks.filter(
        (t) =>
          t.queueType === "approval" &&
          t.plantId === plant.plantId &&
          t.status === "pending"
      );
      return {
        plant,
        count: plantTasks.length,
        urgent: plantTasks.filter((t) => t.priority === "urgent").length,
      };
    });
  }

  get totalPendingCount(): number {
    return this.tasks.filter(
      (t) => t.queueType === "approval" && t.status === "pending"
    ).length;
  }

  get totalPendingAmount(): number {
    return this.tasks
      .filter((t) => t.queueType === "approval" && t.status === "pending")
      .reduce((sum, t) => sum + (t.totalAmount || 0), 0);
  }

  get urgentCount(): number {
    return this.tasks.filter(
      (t) =>
        t.queueType === "approval" &&
        t.status === "pending" &&
        t.priority === "urgent"
    ).length;
  }

  get completedTodayCount(): number {
    const today = new Date().toISOString().split("T")[0];
    return this.tasks.filter(
      (t) =>
        t.queueType === "approval" &&
        t.status === "completed" &&
        t.completedDate === today
    ).length;
  }

  // ==================== Filter Actions ====================

  setPlantFilter = (plantId: number | null) => {
    this.plantFilter = plantId;
  };

  setVendorFilter = (vendor: string) => {
    this.vendorFilter = vendor;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setStatusFilter = (status: "all" | "pending" | "completed") => {
    this.statusFilter = status;
  };

  setPriorityFilter = (priority: "all" | "urgent" | "normal") => {
    this.priorityFilter = priority;
  };

  setAmountFilter = (
    filter: "all" | "under1000" | "1000to5000" | "over5000"
  ) => {
    this.amountFilter = filter;
  };

  setSort = (
    field: "createdDate" | "totalAmount" | "vendorName" | "priority"
  ) => {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
  };

  clearFilters = () => {
    this.plantFilter = null;
    this.vendorFilter = "";
    this.searchTerm = "";
    this.statusFilter = "pending";
    this.priorityFilter = "all";
    this.amountFilter = "all";
  };

  // ==================== Selection ====================

  toggleTaskSelection = (taskId: number) => {
    const index = this.selectedTaskIds.indexOf(taskId);
    if (index >= 0) {
      this.selectedTaskIds.splice(index, 1);
    } else {
      this.selectedTaskIds.push(taskId);
    }
  };

  selectAllVisible = () => {
    this.selectedTaskIds = this.filteredTasks.map((t) => t.taskId);
  };

  clearSelection = () => {
    this.selectedTaskIds = [];
  };

  // ==================== Actions ====================

  openApprovalWizard = (taskId?: number) => {
    this.rootStore.routerStore.goTo("POApprovalWizard");
  };

  // ==================== Data Loading ====================

  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load plants
      if ((this.rootStore as any).plantsStore) {
        await (this.rootStore as any).plantsStore.find();
      }

      // Load tasks
      if ((this.rootStore as any).approvalQueueStore) {
        await (this.rootStore as any).approvalQueueStore.find({
          query: { queueType: "approval" },
        });
      }

      runInAction(() => {
        this.plants = (this.rootStore as any).plantsStore?.entities || [];
        this.tasks = (this.rootStore as any).approvalQueueStore?.entities || [];
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load data";
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.clearFilters();
    this.selectedTaskIds = [];
    this.error = null;
  };
}

export default ApprovalQueueStore;
